// Brain routes: called by the ConsultSession Durable Object in otto-live
// through its service binding to otto-app (design 3.1, 5.3). The voice never
// waits for them; a failed or slow brain call only makes Otto a little less
// targeted. They deploy with otto-app, so brain fixes never drop a call.
//
// The routes are also reachable on otto-app's public hostname, so every
// request must carry BRAIN_AUTH_HEADER with the BRAIN_SHARED_SECRET value;
// otto-app answers 404 (not 401) without it, so the routes are not advertised.
import { z } from "zod";
import { CoachFlag, ConfigPins, type TokenUsage } from "@otto/config";
import { FactStatus, Ledger, LedgerEntry, TranscriptMessage } from "@otto/package-schema";
import { EndReason, Mode } from "@otto/protocol";
import { IsoTime, Ok, route } from "./common";

export const BRAIN_AUTH_HEADER = "x-otto-brain-key" as const;

const Usd = z.number().min(0).nullable();

/** After each finished owner turn. Otto is already replying; nothing waits on this. */
export const BrainTurnRequest = z.strictObject({
  consult_id: z.uuid(),
  config_pins: ConfigPins,
  /** The owner turn that just finished. */
  owner_turn: TranscriptMessage,
  /** Otto's turn right before it, so the guard checks every Otto line (captions and saved turns). */
  otto_turn_before: TranscriptMessage.nullable(),
  ledger: Ledger,
  stage: z.string().min(1).max(64),
  clock_seconds: z.number().int().min(0),
  /** Flags already raised in this consult. */
  flags: z.array(CoachFlag),
  /** Turns left in the humor cooldown (design 5.6). */
  humor_cooldown_left: z.number().int().min(0),
  language: z.enum(["en", "es"]),
});
export type BrainTurnRequest = z.infer<typeof BrainTurnRequest>;

export const BrainTurnResponse = z.object({
  ok: Ok,
  /** Entries to upsert into the ledger (by key, and item for list facts). */
  ledger_updates: z.array(LedgerEntry),
  /**
   * Newly raised flags: each flag is reported ONCE, when it is first raised,
   * EXCEPT two events reported on EVERY turn they happen: `humor_used` (Otto
   * used humor, so the Durable Object restarts its cooldown, design 5.6) and
   * `abusive` (this owner turn was abusive, so the Durable Object counts a
   * strike and closes at `coach.abusive_strikes_to_close`, design 5.8 "code
   * counts strikes"; review fix Sept 28 2026: reported once, the count could
   * never reach two).
   */
  flags: z.array(CoachFlag),
  /**
   * At most one note for Otto's next turn, already filtered by the note rules
   * (none during silent stages, one per Otto turn). The DO sends it as
   * client content with turn_complete false.
   */
  note: z.object({ text: z.string().min(1).max(600) }).nullable(),
  /** Guard results on otto_turn_before; hard gate breaches alert on the app side. */
  guard: z.object({ checked: z.boolean(), hard_gate_failures: z.array(z.string()) }),
  coach: z.enum(["ok", "off", "timeout", "failed"]),
  cost_usd: Usd,
  /** Humor is off for the rest of this call (a humor-off flag was raised). Informational for the meter. */
  humor_off: z.boolean().optional(),
  /**
   * The brain asks the call to close (design 5.8: a prankster gets two more
   * turns, then a short close). The Durable Object counts the owner turns and
   * closes with the reason through its one exit path. null or absent: none.
   */
  end_directive: z
    .object({
      reason: z.literal("conduct"),
      after_owner_turns: z.number().int().min(0).max(5),
    })
    .nullable()
    .optional(),
});
export type BrainTurnResponse = z.infer<typeof BrainTurnResponse>;

/** Near minute 7 (checkpoint) and minute 11 (read-back), triggered by the clock. */
export const BrainReadbackRequest = z.strictObject({
  consult_id: z.uuid(),
  config_pins: ConfigPins,
  kind: z.enum(["checkpoint", "readback"]),
  ledger: Ledger,
  transcript: z.array(TranscriptMessage),
  clock_seconds: z.number().int().min(0),
  language: z.enum(["en", "es"]),
});
export type BrainReadbackRequest = z.infer<typeof BrainReadbackRequest>;

export const BrainReadbackResponse = z.object({
  ok: Ok,
  /** Checked by the guard before it is returned. The DO sends it to Otto as a note. */
  text: z.string().min(1).max(1000),
  source: z.enum(["drafter", "code_fallback"]),
  guard: z.object({ passed: z.boolean(), problems: z.array(z.string()) }),
  cost_usd: Usd,
});
export type BrainReadbackResponse = z.infer<typeof BrainReadbackResponse>;

/**
 * Voice tokens by modality and direction, as the Durable Object meters them
 * from the voice engine's usage reports, summed over the call. These names
 * are the ones priced (voiceTokensToUsage). `cached_input` is a SUBSET of the
 * input counts (Gemini reports cached tokens inside the prompt count,
 * ASSERTED from its API reference), so pricing moves it out of input_text.
 * `output_thoughts` is billed as text output. `input_other` and
 * `output_other` (images, video, or a report without a modality breakdown)
 * cannot be priced: a call that has them is unknown, never $0.
 */
export const VOICE_TOKEN_KEYS = [
  "input_text",
  "input_audio",
  "output_text",
  "output_audio",
  "output_thoughts",
  "cached_input",
  "tool_input",
  "input_other",
  "output_other",
] as const;
export const VoiceTokenKey = z.enum(VOICE_TOKEN_KEYS);
export type VoiceTokenKey = z.infer<typeof VoiceTokenKey>;
export const VoiceTokens = z.partialRecord(VoiceTokenKey, z.number().int().min(0));
export type VoiceTokens = z.infer<typeof VoiceTokens>;

/**
 * Voice tokens as priced parts. `complete` is false when a part that cannot
 * be priced has tokens (then the price is unknown).
 */
export function voiceTokensToUsage(tokens: VoiceTokens): { usage: TokenUsage; complete: boolean } {
  const n = (k: VoiceTokenKey) => Math.max(0, tokens[k] ?? 0);
  const cached = Math.min(n("cached_input"), n("input_text") + n("input_audio"));
  // Cached tokens come out of text input first, then audio input.
  const cachedFromText = Math.min(cached, n("input_text"));
  const cachedFromAudio = cached - cachedFromText;
  const usage: TokenUsage = {};
  const put = (part: keyof TokenUsage, value: number) => {
    if (value > 0) usage[part] = (usage[part] ?? 0) + value;
  };
  put("input", n("input_text") - cachedFromText + n("tool_input"));
  put("audio_input", n("input_audio") - cachedFromAudio);
  put("cached_input", cached);
  put("output", n("output_text") + n("output_thoughts"));
  put("audio_output", n("output_audio"));
  return { usage, complete: n("input_other") === 0 && n("output_other") === 0 };
}

/** One Otto turn's numbers, as the Durable Object measured them (design 9.8). Counts and times only. */
export const LiveTurnMetric = z.object({
  turn: z.number().int().min(0),
  at: IsoTime,
  /** Transcript index of Otto's saved line for this turn; null when the turn saved no words. */
  message_i: z.number().int().min(0).nullable(),
  reply_latency_ms: z.number().min(0).nullable(),
  /** The same wait as the owner heard it, measured in the browser (Oct 1 2026); absent from older otto-live. */
  heard_ms: z.number().min(0).nullable().optional(),
  interrupted: z.boolean(),
  self_interrupted: z.boolean(),
  note_sent: z.boolean(),
  session_swap: z.boolean(),
});
export type LiveTurnMetric = z.infer<typeof LiveTurnMetric>;

/** Events of the live call for the admin replay (design 9.10). Counts and ids only, never words. */
export const LiveEventType = z.enum([
  "connected",
  "disconnected",
  "reconnected",
  "voice_session",
  "voice_goaway",
  "voice_dropped",
  "stage",
  "mode",
  "extension_offered",
  "extension_answered",
  "human_requested",
  "closing",
  "readback_spoken",
]);
export const LiveEvent = z.object({
  at: IsoTime,
  type: LiveEventType,
  detail: z.record(z.string().max(40), z.union([z.number(), z.string().max(64), z.boolean(), z.null()])).nullable(),
});
export type LiveEvent = z.infer<typeof LiveEvent>;

/**
 * Every note, cue and draft that reached Otto (or was dropped), for the
 * replay. The text is coach, clock or drafter text (config and model
 * output), never the owner's words, except where a read-back quotes them.
 */
export const OttoNoteRecord = z.object({
  at: IsoTime,
  /** The newest transcript index when it was sent; null before any line. */
  after_i: z.number().int().min(0).nullable(),
  kind: z.enum(["coach_hint", "clock_note", "readback_note", "checkpoint_note", "ledger_note", "cue"]),
  text: z.string().max(2000),
  delivered: z.boolean(),
  /** Why a note was not delivered (closing, silent_stage, busy, stale, no_voice). */
  dropped_why: z.string().max(40).nullable(),
});
export type OttoNoteRecord = z.infer<typeof OttoNoteRecord>;

/**
 * How the ledger grew: one row per fact update the call applied (design 9.10
 * coverage chart). `item` has the same limit as LedgerEntry.item (240): the
 * timeline copies it from the ledger update, and a tighter limit here made
 * the whole hand-off fail validation forever (review blocker, Sept 28 2026).
 */
export const LEDGER_ITEM_MAX = 240;
export const LedgerTimelineEntry = z.object({
  at: IsoTime,
  /** The owner message the update came from. */
  i: z.number().int().min(0).nullable(),
  key: z.string().min(1).max(64),
  item: z.string().max(LEDGER_ITEM_MAX).nullable(),
  status: FactStatus,
});
export type LedgerTimelineEntry = z.infer<typeof LedgerTimelineEntry>;

/**
 * Most replay rows one hand-off may carry. The Durable Object keeps the first
 * ones when a call has more, so a long call can never make its own hand-off
 * invalid (review fix, Sept 28 2026).
 */
export const END_REPLAY_LIMITS = { turn_metrics: 2000, events: 2000, notes: 1000, ledger_timeline: 2000 } as const;

/**
 * The DO hands the finished consult to otto-app, which writes the transcript
 * and ledger to R2, the consult row to D1 and one queue message (design 3.4
 * step 7). The DO retries until it gets 2xx, then clears its storage later.
 * On withdrew_consent the transcript and ledger are sent EMPTY and nothing
 * is kept (design 9.12).
 */
export const BrainEndRequest = z.strictObject({
  consult_id: z.uuid(),
  config_pins: ConfigPins,
  end_reason: EndReason,
  started_at: IsoTime,
  ended_at: IsoTime,
  connected_seconds: z.number().int().min(0),
  owner_talk_seconds: z.number().int().min(0),
  transcript: z.array(TranscriptMessage),
  ledger: Ledger,
  flags: z.array(CoachFlag),
  readback: z
    .object({
      text: z.string().max(1000),
      source: z.enum(["drafter", "code_fallback"]),
      spoken_i: z.number().int().min(0).nullable(),
    })
    .nullable(),
  extension: z.object({ offered: z.boolean(), accepted: z.boolean().nullable() }),
  mode_final: Mode,
  mode_toggles: z.number().int().min(0),
  reconnects: z.number().int().min(0),
  session_swaps: z.number().int().min(0),
  voice: z.object({
    model: z.string(),
    voice_name: z.string(),
    vad_profile: z.enum(["home", "field", "room"]),
    session_mode: z.enum(["rolling", "compression"]),
  }),
  meter: z.object({
    /** Provider usage by modality and direction, summed over the call (VOICE_TOKEN_KEYS). */
    tokens: VoiceTokens,
    /** Priced by the DO from ConsultInit.spend.voice_price; null when unpriced (unknown, never $0). */
    voice_cost_usd: Usd,
    reply_latency_ms: z.object({ p50: z.number().nullable(), p90: z.number().nullable(), samples: z.number().int().min(0) }),
  }),
  /** Per Otto turn (otto-app writes turn_metrics). Empty on withdrew_consent. */
  turn_metrics: z.array(LiveTurnMetric).max(END_REPLAY_LIMITS.turn_metrics).default([]),
  /** The call's events (otto-app writes consult_events). */
  events: z.array(LiveEvent).max(END_REPLAY_LIMITS.events).default([]),
  /** Notes, cues and drafts sent to Otto, for the replay. Empty on withdrew_consent. */
  notes: z.array(OttoNoteRecord).max(END_REPLAY_LIMITS.notes).default([]),
  /** How the ledger grew. Empty on withdrew_consent. */
  ledger_timeline: z.array(LedgerTimelineEntry).max(END_REPLAY_LIMITS.ledger_timeline).default([]),
});
export type BrainEndRequest = z.infer<typeof BrainEndRequest>;

export const BrainEndResponse = z.object({ ok: Ok, accepted: z.literal(true) });

/**
 * The Durable Object's touch at most once a minute during a call (design 3.4
 * step 5, 3.6): otto-live has no database binding, so otto-app writes
 * consults.last_seen_at (and marks a checked-in consult live).
 */
export const BrainTouchRequest = z.strictObject({
  consult_id: z.uuid(),
  at: IsoTime,
  phase: z.enum(["live", "disconnected", "ended"]),
  clock_seconds: z.number().int().min(0),
  stage: z.string().min(1).max(64),
  agenda_index: z.number().int().min(0),
  mode: Mode,
  connected: z.boolean(),
  end_reason: EndReason.nullable(),
  /** In-call cost known so far; null when unpriced. */
  cost_so_far_usd: Usd,
});
export type BrainTouchRequest = z.infer<typeof BrainTouchRequest>;
export const BrainTouchResponse = z.object({ ok: Ok });

/**
 * A flag that should reach a person NOW, not after the call (design 5.8:
 * the owner asked for a person, a #otto-hot-leads post right away). Sent by
 * the Durable Object when Otto calls flag_human_request. Idempotent per
 * consult and flag.
 */
export const BrainFlagRequest = z.strictObject({
  consult_id: z.uuid(),
  flag: z.literal("asked_for_human"),
  at: IsoTime,
  clock_seconds: z.number().int().min(0),
});
export type BrainFlagRequest = z.infer<typeof BrainFlagRequest>;
export const BrainFlagResponse = z.object({ ok: Ok });

/**
 * The first hello of a consult (design 9.4, 9.5; review fix Sept 28 2026).
 * Before the Durable Object opens a voice session for the first time it asks
 * otto-app whether the call may start NOW: the consult is still checked in
 * (not abandoned), consults are on and not draining (test consults may run
 * while paused, as at check-in), and a seat is free under the concurrency
 * cap. A yes marks the consult live in the same statement that counts the
 * seats, so check-ins banked for hours cannot all start at once. A consult
 * already live answers yes again (a retry, or the owner's new tab) without
 * taking a second seat.
 */
export const BrainAdmitRequest = z.strictObject({
  consult_id: z.uuid(),
  at: IsoTime,
});
export type BrainAdmitRequest = z.infer<typeof BrainAdmitRequest>;
export const BrainAdmitResponse = z.object({
  ok: Ok,
  admit: z.boolean(),
  /** Why not; null when admitted. */
  reason: z.enum(["not_checked_in", "paused", "draining", "at_capacity"]).nullable(),
});
export type BrainAdmitResponse = z.infer<typeof BrainAdmitResponse>;

export const brainRoutes = {
  turn: route({ method: "POST", path: "/brain/turn", auth: "brain", body: BrainTurnRequest, response: BrainTurnResponse }),
  readback: route({
    method: "POST",
    path: "/brain/readback",
    auth: "brain",
    body: BrainReadbackRequest,
    response: BrainReadbackResponse,
  }),
  end: route({ method: "POST", path: "/brain/end", auth: "brain", body: BrainEndRequest, response: BrainEndResponse }),
  touch: route({ method: "POST", path: "/brain/touch", auth: "brain", body: BrainTouchRequest, response: BrainTouchResponse }),
  flag: route({ method: "POST", path: "/brain/flag", auth: "brain", body: BrainFlagRequest, response: BrainFlagResponse }),
  admit: route({ method: "POST", path: "/brain/admit", auth: "brain", body: BrainAdmitRequest, response: BrainAdmitResponse }),
} as const;
